import { inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../environments/environment';
import { Observable, EMPTY, of, firstValueFrom, lastValueFrom } from 'rxjs';
import { tap, catchError, switchMap, filter, take, map } from 'rxjs/operators';
import { CoiSheetsService } from '../service/google-sheets/coi-sheets.service';
import { CommonSheetsService } from '../service/google-sheets/common-sheets.service';
import { NewAttachmentComponent } from './new-attachment/new-attachment.component';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { CreateEntity } from './create-entity';

// Interfaces should be defined outside the class if they are shared.
export interface EntitySelectInterface {
  value: string;
  viewValue: string;
}

/**
 * Modernized abstract base class for Contract-related components.
 * This class handles shared reactive and UI logic.
 */
export abstract class CreateTypeContract extends CreateEntity{
  // Use `protected` to make injected services available to inheriting classes.
  // This is the correct pattern for injecting into an abstract class.
  protected http = inject(HttpClient);
  public sanitizer = inject(DomSanitizer);
  protected coiSheetsService = inject(CoiSheetsService);
  protected commonSheetsService = inject(CommonSheetsService);

  // The state properties are now declared with clear types.
  saving = false;
  readyToSaveParty = false;
  loadingPage = false;

  // File View options
  viewFile = false;
  isLoaded = false;
  pdfSource: any;
  viewFileLinkDisabled = true;
  isOfficeDocument = false;
  officeSourceFile: any;
  // trustedOfficeSource: SafeUrl;
  untrustedOfficeSource: any;
  showPdfUnsafeHtml = null;
  showPdfSafeHtml = null;

  sites: EntitySelectInterface[] = [];
  renewalTypeLabels: any = null;
  serviceTypes: any[] = [];
  statusTypes: any[] = [];
  versionTypes: any[] = [];
  allDepartments: any[] = [];
  allCoverageTypes: any[] = [];

  // this is in case we use the view text feature again
  contractViewMode
  contractFileAsText  

  // This constructor is now empty and can be safely removed by inheriting classes if they don't have their own logic.
  constructor() {
    super();
   }


  addNewPartySimplified() {
    this.readyToSaveParty = !this.readyToSaveParty;
  }

  /**
   * Fetches static field values from the sheets services.
   * This is now fully reactive and returns a single observable.
   */
  public getStaticFieldValues(): Observable<any[]> {

    // let departmentsReponse = await this.googleSheetsService.getDepartments();
    // this.allDepartments = departmentsReponse.values[0];

    // let contractTypesResponse = await this.googleSheetsService.getContractTypes();
    // this.contractTypes = contractTypesResponse.values[0];

    // // let documentTypesResponse = await this.googleSheetsService.workflowDocumentTypes();
    // // this.documentTypes = documentTypesResponse.values[0];
    // // console.log('Document Types: ', this.documentTypes);

    // this.stateOfRecordList = await this.workflowService.getStaticSheetWorkflowColumn('stateOfRecord');
    // this.transactionTypeList = await this.workflowService.getStaticSheetWorkflowColumn('transactionType');
    // this.documentTypesList = await this.workflowService.getStaticSheetWorkflowColumn('documentType');
    // this.phaseList = await this.workflowService.getStaticSheetWorkflowColumn('phase');
    // this.statusList = await this.workflowService.getStaticSheetWorkflowColumn('status');
    // this.thresholdList = await this.workflowService.getStaticSheetWorkflowColumn('valueThreshold');
    // this.workflowPriorityList = await this.workflowService.getStaticSheetWorkflowColumn('priority');    
    
    return this.commonSheetsService.getServiceTypes().pipe(
      switchMap(serviceTypes => {
        this.serviceTypes = serviceTypes;
        return this.commonSheetsService.getContractStatusTypes().pipe(
          tap(statusTypes => this.statusTypes = statusTypes),
          switchMap(() => this.commonSheetsService.getDepartments().pipe(
            tap(departments => this.allDepartments = departments),
            switchMap(() => this.coiSheetsService.getCoverageTypes().pipe(
              tap(coverageTypes => this.allCoverageTypes = coverageTypes),
              map(() => [this.serviceTypes, this.statusTypes, this.allDepartments, this.allCoverageTypes])
            ))
          ))
        );
      }),
      catchError(error => {
        console.error('Error fetching static field values:', error);
        return EMPTY;
      })
    );
  }

  updateMainContractFile(entityId, entityType, entity): void {
    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '800px',
      data: {
        entityId: entityId,
        entityType: entityType,
        entity: entity,
        mainContractFile: true,
        message: 'This will replace the existing contract file!',
        title: 'Add Contract File'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('From dialog =-> ', result);

      if (result) {
        // this.processCoiFileAttachment(result);
      }
    });
  }

  // NOTE: The commented-out code is left as is, as per your request.
  // async addNewMainContractFile(): void {
  //   this.contractAttachment.contracts.push(this.currentBaa.baaNum);
  //   const dialogRef = this.dialog.open(NewAttachmentComponent, {
  //     width: '500px',
  //     data: {
  //       baaNum: this.currentBaa.baaNum,
  //       baa: this.currentBaa,
  //       mainContractFile: true,
  //       message: "This will replace any existing contract file!",
  //       title: "Add Contract File"
  //     }
  //   });

  //   dialogRef.afterClosed().subscribe(async result => {
  //     if (result) {
  //       this.currentBaa.contractFile = result.attachmentNum;
  //       this.currentBaa.contractFileDirectLink = environment.s3Location + result.s3filename;
  //       let contractFileObject = result;
  //       let mainContractFileObject = await this.attachmentUtil.processMainContractFileAttachment(contractFileObject.s3filename);
  //       this.isOfficeDocument = mainContractFileObject['isOfficeDocument'];
  //       if (this.isOfficeDocument) {
  //         this.trustedOfficeSource = mainContractFileObject['safeOfficeFileLink'];
  //       } else {
  //         this.showPdfSafeHtml = mainContractFileObject['safePdfHtml'];
  //       }
  //       this.viewFileLinkDisabled = false;
  //       this.viewFile = true;
  //     }
  //   });
  // }

  moveDepartmentBackToSource(entity, event) {
    if (event.items && event.items.length > 0) {
      event.items.forEach(department => {
        let index = entity.departments.indexOf(department);
        if (index === 0 || index) {
          entity.departments.splice(index, 1);
        }
      });
    }
    this.allDepartments.sort();
  }

  moveSiteBackToSource(entity, event) {
    function siteSortComparator(a, b) {
      let fa = a.name.toLowerCase(),
        fb = b.name.toLowerCase();
      if (fa < fb) {
        return -1;
      }
      if (fa > fb) {
        return 1;
      }
      return 0;
    }

    if (event.items && event.items.length > 0) {
      event.items.forEach(site => {
        let index = entity.sites.findIndex(existingSite => existingSite.name === site.name);
        if (index === 0 || index) {
          entity.sites.splice(index, 1);
        }
      });
    }
    entity.sort(siteSortComparator);
  }

  // NOTE: This function can be improved by using a dedicated service for display logic
  // and by returning an observable instead of setting component state directly.
  processContractFileForDisplay(entityObject) {
    // entityObject.contractFile = entityObject.attachmentNum;
    
    // let fileNameAsArray = entityObject.s3filename.split('.');
    
    // const fileExtension = fileNameAsArray[fileNameAsArray.length - 1]?.toLowerCase();
    
    // this.isOfficeDocument = false; // Reset the flag
    
    // if (fileExtension === 'pdf') {
    //   this.pdfSource = environment.s3Location + entityObject.s3filename;
    //   this.showPdfUnsafeHtml = `<object width="700" height="1000" data="${this.pdfSource}" type="application/pdf"><embed src="${this.pdfSource}" type="application/pdf" /></object>`;
    //   entityObject.fileDirectLink = this.pdfSource;
    // } else if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(fileExtension)) {
    //   this.isOfficeDocument = true;
    //   let officeUrl;
    //   if (fileExtension.startsWith('doc') || fileExtension.startsWith('ppt')) {
    //     officeUrl = 'https://view.officeapps.live.com/op/embed.aspx?src=';
    //   } else {
    //     officeUrl = 'https://excel.officeapps.live.com/op/embed.aspx?src=';
    //   }
    //   this.officeSourceFile = `${officeUrl}${entityObject.linkToFile}${entityObject.s3filename}`;
    //   this.untrustedOfficeSource = this.officeSourceFile;
    // }
  }
}